import React, { useState, useEffect } from "react";
import {
  FileText,
  DollarSign,
  TrendingUp,
  Heart,
  Package,
  RefreshCw,
} from "lucide-react";
import { Link } from "react-router-dom";

let privateLabelLoaded = false;

const benefits = [
  {
    icon: FileText,
    title: "Stand Out with a Unique Brand Identity",
    text: "Building your own brand is essential in today's crowded market. By utilising our private label services, you can design bespoke packaging that instantly catches the eye. Selling premium food products under your own name helps your business separate itself from the competition, ensuring buyers remember exactly who provided them with such exceptional quality.",
  },
  {
    icon: DollarSign,
    title: "Complete Pricing Autonomy",
    text: "When you sell under your own label rather than a third-party brand, you dictate your retail strategy. This gives you the freedom to set prices that align with your specific target audience and local market shifts, empowering you to optimise your revenue streams without being restricted by standard MSRPs (Manufacturer's Suggested Retail Prices).",
  },
  {
    icon: TrendingUp,
    title: "Maximize Your Profit Margins",
    text: "Reselling established brands often means sharing the pie with various middlemen. By sourcing directly through Shree Ambika Global, you cut out those unnecessary intermediaries. This direct supply chain allows you to retain a much larger share of the profits while maintaining competitive retail prices for your end consumers.",
  },
  {
    icon: Heart,
    title: "Cultivate Deep Customer Trust",
    text: "Offering exclusive, customized items under your own banner creates a powerful bond with your buyers. When customers experience the high-quality standards of the items you supply, they associate that excellence entirely with your business. This turns one-time shoppers into devoted, long-term brand advocates who return to you specifically.",
  },
  {
    icon: Package,
    title: "Streamlined Stock and Supply Control",
    text: "Private labeling allows you to order strategically based on your actual sales velocity. Because you dictate your own supply needs, you can keep your inventory lean and responsive. This tailored approach helps you reduce holding costs, match your ordering precisely to customer demand, and prevent product waste.",
  },
  {
    icon: RefreshCw,
    title: "Agility in a Rapidly Changing Market",
    text: "Consumer tastes in the food industry evolve constantly. Partnering with a reliable supplier for your private label gives you the agility to pivot instantly. Whether you want to expand your catalog to capture emerging trends or tweak the packaging of your current offerings, you remain fully adaptable, highly competitive, and always relevant.",
  },
];

const whyUs = [
  {
    title: "Premium Product Range",
    text: "We offer an extensive selection of premium quality food products including spices, seeds, and basmati rice, ready for your custom private label branding.",
  },
  {
    title: "Quality Assurance",
    text: "Our state-of-the-art facilities and strict quality control protocols ensure that every product packed under your brand name meets international standards.",
  },
  {
    title: "Seamless Process",
    text: "From sourcing high-quality ingredients directly from farms to custom packaging, we handle the entire supply chain process smoothly.",
  },
  {
    title: "Flexible Customization",
    text: "Choose from various premium packaging materials, sizes, and custom label designs to perfectly align with your brand identity.",
  },
  {
    title: "Timely Delivery",
    text: "With our efficient logistics and distribution network, we ensure prompt and reliable delivery of your private label shipments.",
  },
];

const products = [
  {
    name: "Cumin Seeds",
    cat: "Spices & Seeds",
    size: "Custom pouch · 50g–5kg",
    img: "product1.png",
  },
  {
    name: "Black Sesame Seeds",
    cat: "Spices & Seeds",
    size: "Custom pouch · 50g–5kg",
    img: `/product2.png`,
  },
  {
    name: "White Sesame Seeds",
    cat: "Spices & Seeds",
    size: "Custom pouch · 50g–5kg",
    img: "product3.png",
  },
  {
    name: "Coriander Seeds",
    cat: "Spices & Seeds",
    size: "Custom pouch · 50g–5kg",
    img: "product4.png",
  },
  {
    name: "Turmeric Finger",
    cat: "Spices & Seeds",
    size: "Custom pouch · 50g–5kg",
    img: "product5.png",
  },
  {
    name: "XXXL Indian Basmati Sella Rice",
    cat: "Basmati Rice",
    size: "Custom pouch · 1kg–25kg",
    img: "product7.png",
  },
  {
    name: "1121 Golden Sella Basmati Rice",
    cat: "Basmati Rice",
    size: "Custom pouch · 1kg–25kg",
    img: "product8.png",
  },
  {
    name: "Super Basmati Rice",
    cat: "Basmati Rice",
    size: "Custom pouch · 1kg–25kg",
    img: "product9.png",
  },
];

function BenefitStop({ item, index }) {
  const Icon = item.icon;
  const isOdd = index % 2 === 0;
  const card = (
    <div className="bg-white border border-black/10 rounded-md p-8">
      <h3 className="font-sans text-2xl  text-[#1E4A3E] mb-3">{item.title}</h3>
      <p className="text-md text-gray-600 leading-relaxed">{item.text}</p>
    </div>
  );
  const bullet = (
    <div className="w-14 h-14 rounded-full bg-[#1E4A3E] flex items-center justify-center mx-auto ring-8 ring-[#EFF4E9] z-10">
      <Icon className="w-6 h-6 text-[#DDA23B]" strokeWidth={1.6} />
    </div>
  );

  return (
    <div className="relative grid md:grid-cols-[1fr_64px_1fr] grid-cols-[56px_1fr] items-center mb-4">
      {isOdd ? (
        <>
          <div className="md:col-start-1 col-start-2">{card}</div>
          <div className="md:col-start-2 col-start-1">{bullet}</div>
          <div className="hidden md:block" />
        </>
      ) : (
        <>
          <div className="md:col-start-2 col-start-1">{bullet}</div>
          <div className="md:col-start-3 col-start-2">{card}</div>
        </>
      )}
    </div>
  );
}

export default function PrivateLabelPage() {
  const [loading, setLoading] = useState(!privateLabelLoaded);

  useEffect(() => {
    if (!privateLabelLoaded) {
      const timer = setTimeout(() => {
        privateLabelLoaded = true;
        setLoading(false);
      }, 400);
      return () => clearTimeout(timer);
    }
  }, []);

  if (loading) {
    return (
      <div className="font-sans text-[#132621] bg-[#FAF7EF] min-h-screen py-24">
        <div className="max-w-6xl mx-auto px-8 animate-pulse space-y-16">
          {/* Hero Skeleton */}
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div className="space-y-4">
              <div className="h-4 bg-gray-200 rounded w-36"></div>
              <div className="h-10 bg-gray-200 rounded w-3/4"></div>
              <div className="h-16 bg-gray-200 rounded w-full"></div>
              <div className="flex gap-4">
                <div className="h-12 bg-gray-200 rounded w-36"></div>
                <div className="h-12 bg-gray-200 rounded w-36"></div>
              </div>
            </div>
            <div className="h-[300px] bg-gray-200 rounded-lg"></div>
          </div>

          {/* Product Offerings Skeleton */}
          <div className="space-y-6">
            <div className="h-8 bg-gray-200 rounded w-48 mx-auto"></div>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-6">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="bg-white rounded-lg p-4 space-y-3 border border-black/5">
                  <div className="h-40 bg-gray-200 rounded"></div>
                  <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                  <div className="h-5 bg-gray-200 rounded w-3/4"></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="font-sans text-[#132621] bg-[#FAF7EF]">
      {/* hero */}
      <section className="relative overflow-hidden py-24">
        <div className="absolute inset-0 bg-[radial-gradient(600px_320px_at_85%_10%,rgba(185,207,174,0.55),transparent_60%),radial-gradient(500px_400px_at_-5%_90%,rgba(221,162,59,0.18),transparent_60%)]" />
        <div className="max-w-6xl mx-auto px-8 relative grid md:grid-cols-2 gap-16 items-center">
          <div>
            <div className="inline-flex items-center gap-2 text-[12.5px] tracking-widest uppercase text-[#C07C1F] font-semibold mb-4">
              <span className="w-6 h-px bg-[#C07C1F] inline-block" /> Private
              Label Program
            </div>
            <h1 className="font-sans text-4xl md:text-5xl leading-tight text-[#1E4A3E] mb-6">
              Your name, on <span className="text-[#B5502E]">every</span> pouch
              we pack.
            </h1>
            <p className="text-gray-600 max-w-md mb-8 text-[17px] leading-relaxed">
              From dehydrated vegetables, spices, pulses to Rice, we grow,
              source and package under one roof — so the only brand your
              customers ever see is yours.
            </p>
            <div className="flex gap-4 flex-wrap">
              <a
                href="#why"
                className="inline-flex items-center gap-2 bg-[#1E4A3E] text-white px-7 py-4 rounded-sm font-semibold text-sm shadow-lg shadow-[#1E4A3E]/30 hover:-translate-y-0.5 transition"
              >
                Explore private labelling
              </a>
              <a
                href="#products"
                className="inline-flex items-center gap-2 border border-[#1E4A3E] text-[#1E4A3E] px-7 py-4 rounded-sm font-semibold text-sm hover:-translate-y-0.5 transition"
              >
                See our product range
              </a>
            </div>
          </div>

          {/* Premium Overlapping Collage in Hero */}
          <div className="relative h-[400px] flex items-center justify-center">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(221,162,59,0.1)_0%,transparent_70%)] pointer-events-none" />

            <div className="relative w-full h-full flex items-center justify-center">
              {/* Left Pouch - Cumin Seeds */}
              <div className="absolute left-[5%] top-[15%] w-[45%] aspect-[3/4] bg-[#FAF7EF] rounded-lg shadow-xl border border-black/5 p-2 rotate-[-6deg] hover:rotate-0 hover:scale-105 hover:z-20 transition duration-300">
                <img
                  src={`${process.env.PUBLIC_URL}/img/partner/product1.png`}
                  alt="Cumin Seeds"
                  className="w-full h-full object-contain"
                />
              </div>

              {/* Right Pouch - Super Basmati Rice */}
              <div className="absolute right-[5%] top-[15%] w-[45%] aspect-[3/4] bg-[#FAF7EF] rounded-lg shadow-xl border border-black/5 p-2 rotate-[6deg] hover:rotate-0 hover:scale-105 hover:z-20 transition duration-300">
                <img
                  src={`${process.env.PUBLIC_URL}/img/partner/product9.png`}
                  alt="Super Basmati Rice"
                  className="w-full h-full object-contain"
                />
              </div>

              {/* Center Pouch - Turmeric Finger */}
              <div className="absolute left-[27.5%] top-[5%] w-[45%] aspect-[3/4] bg-[#FAF7EF] rounded-lg shadow-2xl border border-black/5 p-3 z-10 hover:scale-105 transition duration-300">
                <img
                  src={`${process.env.PUBLIC_URL}/img/partner/product5.png`}
                  alt="Turmeric Finger"
                  className="w-full h-full object-contain"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Us section matching UI details */}
      <section className="bg-white py-20 border-t border-b border-black/5">
        <div className="max-w-6xl mx-auto px-8">
          <div className="text-center mb-16">
            <p className="text-xs tracking-widest uppercase text-[#C07C1F] font-semibold mb-2">
              Our Commitment
            </p>
            <h2 className="font-sans text-3xl md:text-4xl text-[#1E4A3E]">
              Why us?
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-5 gap-6">
            {whyUs.map((item, index) => (
              <div
                key={index}
                className="bg-[#FAF7EF] border border-[#1E4A3E]/10 rounded-lg p-6  text-center shadow-sm hover:shadow-md transition flex flex-col justify-between"
              >
                <div>
                  <div className="w-10 h-10 rounded-full bg-[#1E4A3E]/10 text-[#1E4A3E] flex items-center justify-center mx-auto mb-4 font-sans text-lg font-bold">
                    {index + 1}
                  </div>
                  <h3 className="font-sans text-[18px] font-semibold text-base text-[#1E4A3E] mb-3 leading-snug">
                    {item.title}
                  </h3>
                  <p className="text-[14px] text-gray-600 leading-relaxed">
                    {item.text}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Advantages Timeline section */}
      <section id="why" className="bg-[#EFF4E9] py-24">
        <div className="max-w-6xl mx-auto px-8">
          <div className="max-w-xl mb-14">
            <div className="inline-flex items-center gap-2 text-[12.5px] tracking-widest uppercase text-[#C07C1F] font-semibold mb-3">
              <span className="w-6 h-px bg-[#C07C1F] inline-block" /> Strategic
              Advantages
            </div>
            <h2 className="font-sans text-3xl md:text-4xl text-[#1E4A3E] mb-4">
              Advantages of{" "}
              <span className="text-[#C07C1F]">PRIVATE LABELLING</span> with
              Shree Ambika Global
            </h2>
            <p className="text-gray-600 ">
              Key strategic advantages that help your brand expand margins,
              retain loyalty, and stand out in the marketplace.
            </p>
          </div>

          <div className="relative">
            <div
              className="hidden md:block absolute left-1/2 top-0 bottom-0 w-0.5 -translate-x-1/2"
              style={{
                backgroundImage:
                  "repeating-linear-gradient(to bottom, #DDA23B 0 10px, transparent 10px 20px)",
              }}
            />
            <div
              className="md:hidden absolute left-[27px] top-0 bottom-0 w-0.5"
              style={{
                backgroundImage:
                  "repeating-linear-gradient(to bottom, #DDA23B 0 10px, transparent 10px 20px)",
              }}
            />
            {benefits.map((item, i) => (
              <BenefitStop key={item.title} item={item} index={i} />
            ))}
          </div>
        </div>
      </section>

      {/* products Range section without categories and with real images */}
      <section id="products" className="py-24 bg-white">
        <div className="max-w-6xl mx-auto px-8">
          <div className="max-w-xl mb-14">
            <div className="inline-flex items-center gap-2 text-[12.5px] tracking-widest uppercase text-[#C07C1F] font-semibold mb-3">
              <span className="w-6 h-px bg-[#C07C1F] inline-block" /> Private
              Label Offerings
            </div>
            <h2 className="font-sans text-3xl md:text-4xl text-[#1E4A3E] mb-4">
              Our Product Range
            </h2>
            <p className="text-gray-600">
              Premium grade grains, seeds, and spices ready to carry your unique
              branding and label design.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-8">
            {products.map((p) => (
              <div
                key={p.name}
                className="bg-[#FAF7EF] border border-[#1E4A3E]/10 rounded-lg overflow-hidden hover:-translate-y-1 hover:shadow-xl transition flex flex-col"
              >
                <div className="aspect-[4/3] w-full overflow-hidden bg-white flex items-center justify-center border-b border-black/5 p-4 relative group">
                  <img
                    src={`${process.env.PUBLIC_URL}/img/partner/${p.img}`}
                    alt={p.name}
                    className="max-w-full max-h-full object-contain hover:scale-105 transition duration-300"
                  />
                </div>
                <div className="p-5 flex-1 flex flex-col justify-between">
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-[#C07C1F] font-semibold mb-1">
                      {p.cat}
                    </div>
                    <h4 className="font-sans text-base text-[#1E4A3E] font-medium line-clamp-1">
                      {p.name}
                    </h4>
                  </div>
                  {/* <span className="text-xs text-gray-500 mt-3 block">{p.size}</span> */}
                </div>
              </div>
            ))}
          </div>
          <div className="text-center mt-10">
            <Link
              to="/products"
              className="inline-block shadow shadow-2xl border border-[#1E4A3E]/30 text-[#1E4A3E] rounded px-6 py-2 text-sm font-medium hover:bg-[#1E4A3E] hover:text-white transition"
            >
              & many more.......options are endless
            </Link>
          </div>
        </div>
      </section>

      {/* cta strip */}
      <section className="bg-[#1E4A3E] text-[#EAF1E8] py-16 text-center">
        <div className="max-w-6xl mx-auto px-8">
          <h2 className="font-sans text-white text-3xl mb-3 font-semibold">
            Ready to put your name on the shelf?
          </h2>
          <p className="text-[#C9DBD1] max-w-md mx-auto mb-8">
            Tell us the products, quantities and packaging you have in mind —
            we'll take it from farm to your customer's pantry.
          </p>
          <Link
            to="/contact"
            className="inline-block bg-[#DDA23B] text-[#1E4A3E] px-7 py-4 rounded-sm font-semibold text-sm hover:bg-[#C07C1F] hover:text-white transition"
          >
            Start a Private Label Enquiry
          </Link>
        </div>
      </section>
    </div>
  );
}